import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import AuthLayout from "@/components/auth/AuthLayout";
import LogOut from "@/components/auth/LogOut";
import PricingRedirect from "@/components/marketing/PricingRedirect";
import CLILogin from "@/components/auth/CLILogin";
import { StoaAuthPage } from "@/components/auth/StoaAuthPage";
import NotFound from "@/components/error/NotFound";
import ServerError from "@/components/error/ServerError";
import ServerErrorBoundary from "@/components/error/ServerErrorBoundary";
import ServerErrorInitializer from "@/components/error/ServerErrorInitializer";
import { ServerError as ServerErrorClass } from "@/types/errors";
import ExtensionLogin from "./components/auth/ExtensionLogin";
import { useAuthStatus } from "@/contexts/OptimizedAuthContext";
import { usePostHogPageView } from "@/lib/analytics";
import { SearchChatModeProvider } from "@/contexts/SearchChatModeProvider";
import {
  EntitlementProvider,
} from "@/contexts/EntitlementContext";
import { FlagsProvider, RequireFlag } from "@/contexts/FlagsContext";
import { SearchChatCache } from "@/lib/cache/searchChatCache";
import { AppShell } from "@/components/layout/AppShell";

// const AppExplorer = lazy(() => import("@/components/explorer/AppExplorer"));
// LayoutManager is the heaviest route component: it statically pulls in the
// markdown/syntax-highlighting stack (highlight.js ~919KB) + framer-motion.
// Lazy-loading it keeps all of that off the initial entry chunk; the AppShell
// Outlet provides the Suspense boundary so the ribbon stays put while it loads.
const LayoutManager = lazy(() => import("@/components/layout/LayoutManager"));
const ApiDocs = lazy(() => import("@/components/api/ApiDocs"));
const ResumeHub = lazy(() =>
  import("@/components/resume/ResumeHub").then((m) => ({ default: m.ResumeHub }))
);

const ProjectPage = lazy(() => import("@/components/projects/ProjectPage"));
const AllChatsPage = lazy(() => import("@/components/chat/AllChatsPage"));
const AllProjectsPage = lazy(
  () => import("@/components/projects/AllProjectsPage")
);
import { AnalyticsGate } from "@/components/analytics/AnalyticsGate";
import { TeamsGate } from "@/components/teams/TeamsGate";
const AnalyticsOverview = lazy(
  () => import("@/components/analytics/AnalyticsOverview")
);
const ActivityTab = lazy(
  () => import("@/components/analytics/tabs/ActivityTab")
);
const MessagesTab = lazy(
  () => import("@/components/analytics/tabs/MessagesTab")
);
const LedgerTab = lazy(
  () => import("@/components/analytics/tabs/LedgerTab")
);

import { SkillsGate } from "@/components/skills/SkillsGate";
const SuperadminSkillsPage = lazy(
  () => import("@/components/admin/SuperadminSkillsPage")
);
const Settings = lazy(() =>
  import("@/components/settings/Settings").then((m) => ({ default: m.Settings }))
);

const SharedSessionView = lazy(
  () => import("@/components/session/SharedSessionView")
);

const TeamsListView = lazy(() =>
  import("@/components/teams/TeamsListView").then((m) => ({
    default: m.TeamsListView,
  }))
);
const TeamDetailView = lazy(() =>
  import("@/components/teams/TeamDetailView").then((m) => ({
    default: m.TeamDetailView,
  }))
);
const InviteAcceptView = lazy(() =>
  import("@/components/teams/InviteAcceptView").then((m) => ({
    default: m.InviteAcceptView,
  }))
);

const CheckoutRedirect = lazy(
  () => import("@/components/billing/CheckoutRedirect")
);
const BillingPage = lazy(() => import("@/components/billing/BillingPage"));
const TrialWelcome = lazy(() => import("@/components/billing/TrialWelcome"));

// Components
import { ProtectedRoute } from "./components";

function AppRoutes() {
  const { isSignedIn, isLoaded } = useAuthStatus();
  usePostHogPageView();

  return (
    <ServerErrorBoundary>
      <ServerErrorInitializer />
      <Routes>
        {/* Debug route only available in development */}
        {import.meta.env.DEV && (
          <Route
            path="/debug-sentry"
            element={
              <button
                type="button"
                onClick={() => {
                  throw new Error("Sentry Test Error");
                }}
              >
                Break the world
              </button>
            }
          />
        )}
        {/* 500 route for server redirects and testing */}
        <Route
          path="/500"
          element={<ServerError error={new ServerErrorClass(500, "Internal Server Error")} />}
        />
        <Route path="/auth" element={<AuthLayout />} />
        <Route path="/logout" element={<LogOut />} />
        {/* Marketing gateway — plants the shared PostHog identity cookie,
            then forwards to specstory.com/pricing. Public, no auth. */}
        <Route path="/pricing" element={<PricingRedirect />} />
        {/* Public share route - no authentication required */}
        <Route
          path="/share/:sessionId"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <SharedSessionView />
            </Suspense>
          }
        />
        {/* Public team invite landing — token-guarded, no auth required to preview.
            Accepting requires sign-in (handled inside the view). */}
        <Route
          path="/invite/:token"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <InviteAcceptView />
            </Suspense>
          }
        />
        {/* Auth handoff + docs: signed-in but intentionally outside the app shell. */}
        <Route
          path="/cli-login"
          element={
            <ProtectedRoute>
              <CLILogin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/extension-login"
          element={
            <ProtectedRoute>
              <ExtensionLogin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-docs"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <ApiDocs />
            </Suspense>
          }
        />
        <Route path="/stoa-sign-in" element={<StoaAuthPage mode="sign-in" />} />
        <Route path="/stoa-sign-up" element={<StoaAuthPage mode="sign-up" />} />

        {/* ---- Authenticated app shell (persistent NavigationRibbon) ---- */}
        <Route element={<AppShell />}>
          <Route
            path="/"
            element={
              // Redirect to /auth if not logged in, else show LayoutManager
              isLoaded ? (
                isSignedIn ? (
                  <ProtectedRoute>
                    <LayoutManager />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/auth" replace />
                )
              ) : (
                <div>Loading...</div>
              )
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <LayoutManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <AllProjectsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <ProjectPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/sessions/:sessionId"
            element={
              <ProtectedRoute>
                <LayoutManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chats"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <AllChatsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chats/:chatId"
            element={
              <ProtectedRoute>
                <LayoutManager />
              </ProtectedRoute>
            }
          />
          {/* Analytics: open to everyone (auth + flag only). AnalyticsGate renders
              the full AnalyticsLayout for entitled users and ONLY an upgrade CTA +
              dismissable education for non-Pro viewers. Nested tab routes below render
              through AnalyticsLayout's <Outlet/> and are suppressed for non-Pro. The
              admin analytics route (/admin/analytics/:userId) is separately staff-gated
              and deliberately NOT Pro-gated. */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <RequireFlag flag="analytics">
                  <AnalyticsGate />
                </RequireFlag>
              </ProtectedRoute>
            }
          >
            <Route index element={<AnalyticsOverview />} />
            <Route path="activity" element={<ActivityTab />} />
            <Route path="messages" element={<MessagesTab />} />
            <Route path="ledger" element={<LedgerTab />} />
          </Route>
          <Route
            path="/projects/:projectId/sessions/:sessionId/chats/:chatId"
            element={
              <ProtectedRoute>
                <LayoutManager />
              </ProtectedRoute>
            }
          />
          {/* Settings hub (P6): Account · API Keys · Connected tools. /api-keys
              redirects into it for back-compat. */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <Settings />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/api-keys" element={<Navigate to="/settings?tab=api-keys" replace />} />
          {/* Teams: dark-launched behind the `teams` flag; open to everyone the flag
              is on for, with TeamsGate rendering the view for Team-plan users and an
              upgrade CTA for everyone else (mirrors AnalyticsGate). */}
          <Route
            path="/teams"
            element={
              <ProtectedRoute>
                <RequireFlag flag="teams">
                  <Suspense fallback={<div>Loading...</div>}>
                    <TeamsGate>
                      <TeamsListView />
                    </TeamsGate>
                  </Suspense>
                </RequireFlag>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams/:slug"
            element={
              <ProtectedRoute>
                <RequireFlag flag="teams">
                  <Suspense fallback={<div>Loading...</div>}>
                    <TeamsGate>
                      <TeamDetailView />
                    </TeamsGate>
                  </Suspense>
                </RequireFlag>
              </ProtectedRoute>
            }
          />
          {/* Resume (Pro). The route is open to everyone (auth + flag only);
              ResumeHub renders in-page upgrade messaging for non-Pro viewers
              instead of gating with RequireFeature. Full picker lands in P5; this
              teaches the capability. */}
          <Route
            path="/resume"
            element={
              <ProtectedRoute>
                <RequireFlag flag="resume">
                  <Suspense fallback={<div>Loading...</div>}>
                    <ResumeHub />
                  </Suspense>
                </RequireFlag>
              </ProtectedRoute>
            }
          />
          {/* Skills: open to everyone (auth + flag only). SkillsGate renders the
              full SkillsWorkspace for entitled users and ONLY an upgrade CTA +
              dismissable education for non-Pro viewers. Legacy /skills/installed
              and /skills/runs redirect into the workspace. The admin skills route
              (/admin/skills) is separately staff-gated. */}
          <Route
            path="/skills"
            element={
              <ProtectedRoute>
                <RequireFlag flag="skills">
                  <SkillsGate />
                </RequireFlag>
              </ProtectedRoute>
            }
          />
          {/* Cloud Skills IA: Installed is now a state filter inside the workspace. */}
          <Route path="/skills/installed" element={<Navigate to="/skills?state=installed" replace />} />
          {/* Cloud Skills IA: Activity is a tab inside the workspace now. */}
          <Route path="/skills/runs" element={<Navigate to="/skills?tab=activity" replace />} />
          {/* Short-path convenience redirects into the unified workspace. */}
          <Route path="/runs" element={<Navigate to="/skills?tab=activity" replace />} />
          {/* Superadmin Skills interface (SpecStory staff only). The page and its
              API are both gated by the @specstory.com email domain; a non-staff
              signed-in user lands on a "Not authorized" panel. */}
          <Route
            path="/admin/skills"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <SuperadminSkillsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Selecting a user routes here — the same master-detail shell, with that
              user selected in the main frame (rail stays mounted). Deep-linkable. */}
          <Route
            path="/admin/skills/:userId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <SuperadminSkillsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Superadmin Analytics: view a selected user's analytics through the
              REAL AnalyticsLayout (nested tab routes reuse the same lazy tab
              components). Staff-gated like /admin/skills; deliberately NOT
              Pro-gated — the admin endpoints skip the entitlement check so any
              user's view (incl. free tier) is inspectable. */}
          <Route
            path="/admin/analytics/:userId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <SuperadminSkillsPage section="analytics" />
                </Suspense>
              </ProtectedRoute>
            }
          >
            <Route index element={<AnalyticsOverview />} />
            <Route path="activity" element={<ActivityTab />} />
            <Route path="messages" element={<MessagesTab />} />
            <Route path="ledger" element={<LedgerTab />} />
          </Route>
          <Route path="/admin" element={<Navigate to="/admin/skills" replace />} />
          <Route path="/installed" element={<Navigate to="/skills?state=installed" replace />} />
          {/* Billing: account-first checkout (signed in via ProtectedRoute, then
              redirected to Stripe) + the manage/cancel page. */}
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <RequireFlag flag="billing">
                  <Suspense fallback={<div>Loading...</div>}>
                    <CheckoutRedirect />
                  </Suspense>
                </RequireFlag>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <RequireFlag flag="billing">
                  <Suspense fallback={<div>Loading...</div>}>
                    <BillingPage />
                  </Suspense>
                </RequireFlag>
              </ProtectedRoute>
            }
          />
          {/* Pricing-experiment post-checkout welcome (free 60-day trial). */}
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <RequireFlag flag="billing">
                  <Suspense fallback={<div>Loading...</div>}>
                    <TrialWelcome />
                  </Suspense>
                </RequireFlag>
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </ServerErrorBoundary>
  );
}

function App() {
  // Clean up old cache entries on app initialization
  useEffect(() => {
    SearchChatCache.cleanup();
  }, []);

  return (
    <Router>
      <EntitlementProvider>
        <FlagsProvider>
          <SearchChatModeProvider>
            <AppRoutes />
          </SearchChatModeProvider>
        </FlagsProvider>
      </EntitlementProvider>
    </Router>
  );
}

export default App;
